import { useAppContext } from '../../context/AppContext';
import { fetchRunBatch } from '../../services';
import { useState } from 'react';

const PROCESS_OPTIONS = [
  { value: 'direct_process', label: 'Direct Process' },
  { value: 'consolidated_process', label: 'Consolidated Process' },
];

function initialState() {
  return {
    startDate: '',
    endDate: '',
    processToRun: '',
    submitted: false,
    saving: false,
  };
}

export const BatchXero = ({ isOpen, onClose }) => {
  const { token, base_url, showStatus } = useAppContext();
  const [state, setState] = useState(initialState);

  if (!isOpen) return null;

  const resetAndClose = () => {
    setState(initialState());
    onClose?.();
  };

  const setField = (key, value) => {
    setState((prev) => ({ ...prev, [key]: value }));
  };

  const runBatch = async (e) => {
    e.preventDefault();
    if (!state.startDate || !state.endDate || !state.processToRun) {
      showStatus('error', 'Choose a start date, end date, and process before submitting.');
      return;
    }

    setField('saving', true);

    try {
      await fetchRunBatch(token, base_url, {
        startDate: state.startDate,
        endDate: state.endDate,
        processToRun: state.processToRun,
      });
      setState((prev) => ({
        ...prev,
        startDate: '',
        endDate: '',
        processToRun: '',
        submitted: true,
      }));
      showStatus('success', 'Batch submitted. We will email you when it is complete.');
    } catch (error) {
      showStatus('error', error.message ?? 'Failed to submit batch.');
    } finally {
      setField('saving', false);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={resetAndClose} aria-hidden="true" />
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="batch-xero-modal-title">
        <div className="modal-content batch-modal-content">
          <h2 id="batch-xero-modal-title" className="modal-title">
            {!state.submitted ? "Let's get your batch running" : 'Batch running...'}
          </h2>
          <div className="divider" />

          {!state.submitted ? (
            <form onSubmit={runBatch} className="batch-modal-form">
              <div className="form-group">
                <label className="form-label" htmlFor="batch-xero-start-date">
                  Select a Starting Date
                </label>
                <input
                  id="batch-xero-start-date"
                  className="form-input"
                  type="date"
                  value={state.startDate}
                  onChange={(e) => setField('startDate', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="batch-xero-end-date">
                  Select an End Date
                </label>
                <input
                  id="batch-xero-end-date"
                  className="form-input"
                  type="date"
                  value={state.endDate}
                  onChange={(e) => setField('endDate', e.target.value)}
                />
              </div>

              <div className="form-group">
                <p className="form-label batch-modal-label-spaced">Select Process to Run</p>
                <div className="batch-radio-group" role="radiogroup" aria-label="Select process to run">
                  {PROCESS_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className="batch-radio-item"
                      htmlFor={`batch-xero-process-${option.value}`}
                    >
                      <input
                        id={`batch-xero-process-${option.value}`}
                        type="radio"
                        name="processToRun"
                        value={option.value}
                        checked={state.processToRun === option.value}
                        onChange={(e) => setField('processToRun', e.target.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary batch-modal-submit"
                disabled={state.saving}
              >
                {state.saving ? 'Submitting…' : 'Run Batch'}
              </button>
            </form>
          ) : (
            <p className="modal-body batch-modal-message">
              We will send you an email when the batch is complete.
            </p>
          )}

          <button
            type="button"
            className="btn btn-secondary batch-modal-close"
            onClick={resetAndClose}
          >
            {!state.submitted ? 'Cancel' : 'Close'}
          </button>
        </div>
      </div>
    </>
  );
};
